/**
 * IndexedDB-backed per-peer consent store for profile-sharing decisions.
 *
 * Key space: `ps:<peerUserId>` — one record per peer, stored as JSON.
 * The store drives the four-state machine; every write is an upsert that
 * replaces the prior record for that peer.
 *
 * State machine (matches iOS ProfileShareStore.swift exactly):
 *
 *   (no record) → undecided   (implicit; `getDecision` returns 'undecided')
 *   undecided   → sharing | declined       (both valid)
 *   sharing     → revoked | declined       (both valid)
 *   sharing     → sharing                  (idempotent — returns existing)
 *   declined    → sharing                  (user changed mind)
 *   declined    → declined                 (idempotent)
 *   declined    → revoked                  (INVALID — .invalidTransition)
 *   revoked     → sharing                  (re-enrols with new key hash)
 *   revoked     → declined                 (INVALID — .invalidTransition)
 *   revoked     → revoked                  (INVALID — .invalidTransition)
 *   undecided   → revoked                  (INVALID — .invalidTransition)
 *
 * Mirrors kaether_for_ios/Stoat/E2EE/ProfileShareStore.swift.
 */

import type {
  ProfileShareDecision,
  ProfileShareError,
  ProfileShareRecord,
  ProfileShareResult,
} from "./ProfileShareConsent";

// ---------------------------------------------------------------------------
// SHA-256 helper (Web Crypto API — available in both browser and SW contexts)
// ---------------------------------------------------------------------------

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const hashBuf = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(hashBuf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// ---------------------------------------------------------------------------
// IndexedDB helpers
// ---------------------------------------------------------------------------

const DB_NAME = "kaether:profile-share:v1";
const STORE_NAME = "decisions";
const DB_VERSION = 1;

function openDB(dbName: string = DB_NAME): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_NAME);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbGet(
  db: IDBDatabase,
  key: string,
): Promise<ProfileShareRecord | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(key);
    req.onsuccess = () => resolve(req.result as ProfileShareRecord | undefined);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(
  db: IDBDatabase,
  key: string,
  value: ProfileShareRecord,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).put(value, key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

function idbGetAll(db: IDBDatabase): Promise<ProfileShareRecord[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () =>
      resolve(req.result as ProfileShareRecord[]);
    req.onerror = () => reject(req.error);
  });
}

function idbClear(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function invalidTransitionError(
  from: ProfileShareDecision,
  action: string,
): ProfileShareError {
  return { kind: "invalidTransition", from, action };
}

function now(): string {
  return new Date().toISOString();
}

// ---------------------------------------------------------------------------
// ProfileShareStore
// ---------------------------------------------------------------------------

export class ProfileShareStore {
  private readonly dbName: string;

  /**
   * Shared application instance. Use a custom `dbName` in tests to get
   * isolated storage without touching production state.
   */
  static readonly shared = new ProfileShareStore();

  constructor(dbName: string = DB_NAME) {
    this.dbName = dbName;
  }

  // --------------------------------------------------------------------------
  // Queries
  // --------------------------------------------------------------------------

  /** Existing record for the peer, or `undefined` if never persisted. */
  async getRecord(
    peerUserId: string,
  ): Promise<ProfileShareRecord | undefined> {
    const db = await openDB(this.dbName);
    try {
      return await idbGet(db, peerUserId);
    } finally {
      db.close();
    }
  }

  /**
   * Defaults to `'undecided'` when no record exists — the prompt policy
   * treats absence and undecided identically.
   */
  async getDecision(peerUserId: string): Promise<ProfileShareDecision> {
    const rec = await this.getRecord(peerUserId);
    return rec?.decision ?? "undecided";
  }

  /**
   * All peers currently in `'sharing'` state, sorted by `decidedAt`
   * descending (most-recently shared first).
   */
  async peersSharing(): Promise<ProfileShareRecord[]> {
    const all = await this.getAllRecords();
    return all
      .filter((r) => r.decision === "sharing")
      .sort((a, b) => b.decidedAt.localeCompare(a.decidedAt));
  }

  /**
   * All peers currently in `'revoked'` state, sorted by `decidedAt`
   * descending.
   */
  async revokedPeers(): Promise<ProfileShareRecord[]> {
    const all = await this.getAllRecords();
    return all
      .filter((r) => r.decision === "revoked")
      .sort((a, b) => b.decidedAt.localeCompare(a.decidedAt));
  }

  private async getAllRecords(): Promise<ProfileShareRecord[]> {
    const db = await openDB(this.dbName);
    try {
      return await idbGetAll(db);
    } finally {
      db.close();
    }
  }

  // --------------------------------------------------------------------------
  // Transitions
  // --------------------------------------------------------------------------

  /**
   * User chose to share their profile with this peer.
   *
   * Valid from: `'undecided'`, `'declined'`, `'revoked'`.
   * Idempotent from `'sharing'` — returns the existing record unchanged.
   *
   * Stores a SHA-256 hex digest of the raw profile-key bytes so a future
   * audit can identify "which key was shared with this peer" without the
   * raw key being recoverable from the store.
   *
   * @param peerUserId  The peer to share with.
   * @param profileKeyBytes  Raw bytes of the profile key being shared.
   *                         NEVER stored directly; only its SHA-256 hash
   *                         is persisted.
   * @param atIso  Optional ISO-8601 timestamp (defaults to `now()`).
   */
  async markSharing(
    peerUserId: string,
    profileKeyBytes: Uint8Array,
    atIso?: string,
  ): Promise<ProfileShareResult> {
    if (!peerUserId) {
      return { ok: false, error: { kind: "emptyPeerUserId" } };
    }

    const existing = await this.getRecord(peerUserId);
    if (existing?.decision === "sharing") {
      // Idempotent no-op — do not churn decidedAt or key hash.
      return { ok: true, record: existing };
    }

    const keyHash = await sha256Hex(profileKeyBytes);
    const ts = atIso ?? now();
    const record: ProfileShareRecord = {
      peerUserId,
      decision: "sharing",
      decidedAt: ts,
      revokedAt: null,
      sharedProfileKeyHash: keyHash,
    };
    await this.put(record);
    return { ok: true, record };
  }

  /**
   * User chose NOT to share (or chose to stop sharing without full
   * revocation). Valid from `'undecided'` or `'sharing'`.
   * Idempotent from `'declined'`.
   * INVALID from `'revoked'` — revoked is a stronger statement.
   *
   * Preserves any previously-recorded key hash for historical accuracy.
   *
   * @param atIso  Optional ISO-8601 timestamp (defaults to `now()`).
   */
  async markDeclined(
    peerUserId: string,
    atIso?: string,
  ): Promise<ProfileShareResult> {
    if (!peerUserId) {
      return { ok: false, error: { kind: "emptyPeerUserId" } };
    }

    const existing = await this.getRecord(peerUserId);
    if (existing?.decision === "declined") {
      return { ok: true, record: existing };
    }
    if (existing?.decision === "revoked") {
      return {
        ok: false,
        error: invalidTransitionError("revoked", "markDeclined"),
      };
    }

    const prevHash = existing?.sharedProfileKeyHash ?? null;
    const ts = atIso ?? now();
    const record: ProfileShareRecord = {
      peerUserId,
      decision: "declined",
      decidedAt: ts,
      revokedAt: null,
      sharedProfileKeyHash: prevHash,
    };
    await this.put(record);
    return { ok: true, record };
  }

  /**
   * User chose to revoke an active share. Valid ONLY from `'sharing'`.
   *
   * From any other state returns `invalidTransition` so the caller can
   * surface a clearer UX ("you weren't sharing with this person to begin
   * with"). The parallel ProfileKeyRotation feature performs the actual
   * key rotation; this store only records the user's intent.
   *
   * @param atIso  Optional ISO-8601 timestamp (defaults to `now()`).
   */
  async markRevoked(
    peerUserId: string,
    atIso?: string,
  ): Promise<ProfileShareResult> {
    if (!peerUserId) {
      return { ok: false, error: { kind: "emptyPeerUserId" } };
    }

    const existing = await this.getRecord(peerUserId);
    const currentDecision: ProfileShareDecision =
      existing?.decision ?? "undecided";
    if (currentDecision !== "sharing") {
      return {
        ok: false,
        error: invalidTransitionError(currentDecision, "markRevoked"),
      };
    }

    const ts = atIso ?? now();
    const record: ProfileShareRecord = {
      peerUserId,
      decision: "revoked",
      decidedAt: ts,
      revokedAt: ts,
      sharedProfileKeyHash: existing!.sharedProfileKeyHash,
    };
    await this.put(record);
    return { ok: true, record };
  }

  // --------------------------------------------------------------------------
  // Reset (testing / "delete my account")
  // --------------------------------------------------------------------------

  /** Wipe all records. Use only in tests or "delete account" flows. */
  async resetAll(): Promise<void> {
    const db = await openDB(this.dbName);
    try {
      await idbClear(db);
    } finally {
      db.close();
    }
  }

  // --------------------------------------------------------------------------
  // Storage primitive
  // --------------------------------------------------------------------------

  private async put(record: ProfileShareRecord): Promise<void> {
    const db = await openDB(this.dbName);
    try {
      await idbPut(db, record.peerUserId, record);
    } finally {
      db.close();
    }
  }
}
